// src/features/org-analytics/schemas/shared.ts
import { z } from 'zod';

// Filter / query enums (BE: src/schemas/orgAnalyticsSchema.ts)
export const projectEnum = z.enum([
  'overall',
  'campus_support',
  'helpline_support',
  'portal_helpline', // BE accepts this 4th value (Q8.1a — see spec §3 / §6.1)
]);
export type Project = z.infer<typeof projectEnum>;

export const dateRangeEnum = z.enum([
  // Short quick-ranges — surfaced through the quick-range dropdown, not the pills.
  'today',
  'yesterday',
  'last-7-days',
  'last-14-days',
  // Pill toolbar ranges. 'last-30-days' is shared: it is both the 30D pill and
  // the last quick-range option, and both write the same value.
  'last-30-days',
  'last-3-months',
  'last-6-months',
  'this-year',
  'all-time',
  'custom',
]);
export type DateRange = z.infer<typeof dateRangeEnum>;

// Domain enums (BE: org-user-analytics doc §1.1). The concern taxonomy has one
// definition in the app, the session picker's, and analytics reads exactly the
// keys those pickers write, so a second copy here could only ever drift.
// `bipolar_disorder` is corporate-only and collapses to "Others" on campus.
export { concernEnum } from '@/features/sessions/constants/concerns';

export const referralSourceEnum = z.enum([
  'self_referral',
  'staff_faculty',
  'peer_referral',
  'parent_referral',
  'dean_referral',
  'others',
]);

export const riskProfileEnum = z.enum([
  'critical',
  'very_high',
  'high',
  'medium',
  'low',
  // Retired by migration 0068 — data moved to `low`. Still parsed so a stale row
  // from an older build does not fail payload validation.
  'very_low',
  'none',
  'unknown',
]);

export const flagTypeEnum = z.enum(['crisis', 'acute_risk', 'suicidal_ideation']);

// Programme is free text, not an enum: it is the snapshot of a per-location
// program catalog name, so the BE groups on whatever the location called it.
export const programmeSchema = z.string();

export const orgTypeEnum = z.enum(['institution', 'corporate']);

export const genderEnum = z.enum(['Male', 'Female', 'Other']);

// Display-bucket enums (BE returns these labels in payload `label` fields)
export const riskLevelLabel = z.enum(['Critical', 'High', 'Medium', 'Low', 'Unknown']);

// Meta (returned on every endpoint)
export const metaSchema = z.object({
  orgId: z.string(),
  locationId: z.string().nullable(),
  orgType: orgTypeEnum,
  project: projectEnum,
  dateRange: dateRangeEnum,
  windowStart: z.string(), // ISO
  windowEnd: z.string(),
  comparisonStart: z.string(),
  comparisonEnd: z.string(),
  comparisonMode: z.literal('prior_window'),
  generatedAt: z.string(),
});
export type Meta = z.infer<typeof metaSchema>;

// Response envelope (BE wraps everything in this)
export function envelopeOf<T extends z.ZodType>(dataSchema: T) {
  return z.object({
    status: z.literal('success'),
    message: z.string(),
    data: dataSchema,
  });
}

// Error body shape (per spec §2.3)
export const errorEnvelopeSchema = z.object({
  status: z.literal('error'),
  message: z.string(),
  errors: z.array(z.string()).optional(),
});
export type ErrorEnvelope = z.infer<typeof errorEnvelopeSchema>;

// Filter state held in URL search params (subset — only set keys serialize)
export const filterStateSchema = z.object({
  dateRange: dateRangeEnum.optional(),
  project: projectEnum.optional(),
  locationId: z.string().optional(),
  customFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .optional(),
  customTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .optional(),
});
export type FilterState = z.infer<typeof filterStateSchema>;

// Defaults applied when reading: omitted = backend default
export const FILTER_DEFAULTS = {
  dateRange: 'last-3-months' as const,
  project: 'overall' as const,
} satisfies Partial<FilterState>;
